import type { BoardRow } from '@/services/arcade';

interface LeaderboardProps {
  rows: BoardRow[];
  viewer: BoardRow | null;
  onReport: (address: string) => void;
}

// Mirrors PlayerName.tsx's avatar treatment (pixel-corners + bg-pixel-cyan +
// initials fallback) at leaderboard scale — 14px instead of 46px.
function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '☕';

  // Lite members have no profile, so the server names them by shortened
  // address (api/arcade.ts: `${address.slice(0, 6)}…${address.slice(-4)}`,
  // e.g. "0x1234…5678") — a single no-space "word". Splitting that on
  // whitespace and taking each word's first letter yields just "0" for
  // every such player, since every address starts "0x": every lite member
  // would show the same undifferentiated avatar on a board whose entire
  // purpose is telling players apart. Pull two characters that actually
  // vary instead — the first two hex digits after the "0x" prefix.
  if (!/\s/.test(trimmed) && /^0x[0-9a-fA-F]/.test(trimmed)) {
    return trimmed.slice(2, 4).toUpperCase();
  }

  const initials = trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return initials || '☕';
}

function Avatar({ row }: { row: BoardRow }) {
  return (
    <div className="flex-none w-[14px] h-[14px] pixel-corners-sm bg-pixel-cyan flex items-center justify-center font-grotesk font-bold text-[6px] text-teal-ink overflow-hidden">
      {row.avatarUrl ? (
        <img
          src={row.avatarUrl}
          alt=""
          className="w-[14px] h-[14px] object-cover"
          style={{ imageRendering: 'pixelated' }}
        />
      ) : (
        initialsFor(row.name)
      )}
    </div>
  );
}

// The report control is per-row (not global) because it needs the row's own
// address — and it's gated on `row.isViewer`, not on comparing against the
// `viewer` prop, so the pinned row (which IS the viewer) also correctly
// suppresses its own report button without extra plumbing.
function Row({ row, onReport }: { row: BoardRow; onReport: (address: string) => void }) {
  return (
    <div
      className={`flex items-center gap-2.5 px-[14px] py-[8px] min-w-0 ${row.isViewer ? 'bg-[#fff5ec]' : ''}`}
    >
      <span className="font-heading text-[9px] text-body-grey w-[20px] shrink-0">{row.rank}</span>
      <Avatar row={row} />
      <span className="font-grotesk font-bold text-[13px] text-primary truncate flex-1 min-w-0">
        {row.name}
      </span>
      <span className="font-heading text-[11px] text-coral shrink-0">{row.score}</span>
      {!row.isViewer && (
        <button
          type="button"
          aria-label={`report ${row.name}`}
          onClick={() => onReport(row.address)}
          className="flex-none text-body-grey"
        >
          ⚑
        </button>
      )}
    </div>
  );
}

// Weekly board: top 25 (`rows`) plus the caller's own standing (`viewer`),
// which the /api/arcade board endpoint attaches unconditionally — even when
// that player is already inside `rows`. Rendering the pinned block whenever
// `viewer` is merely non-null would double-render that common case, so
// presence is checked explicitly by address (the project's identity key,
// per PlayerName.tsx: "identity is the wallet address") against `rows`
// before deciding to pin.
export default function Leaderboard({ rows, viewer, onReport }: LeaderboardProps) {
  const viewerPinned = viewer != null && !rows.some((r) => r.address === viewer.address);

  return (
    <div className="bg-white pixel-corners overflow-hidden">
      {rows.length === 0 ? (
        <p className="font-heading text-[9px] text-body-grey text-center px-[18px] py-[24px]">
          No scores yet
        </p>
      ) : (
        rows.map((row) => <Row key={row.address} row={row} onReport={onReport} />)
      )}
      {viewerPinned && viewer && (
        <>
          <div className="h-px bg-hairline" />
          <Row row={viewer} onReport={onReport} />
        </>
      )}
    </div>
  );
}
